import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight, Eye, EyeOff, Check } from 'lucide-react';
import { motion } from 'framer-motion';

const features = [
  'Manage PAF documents with drag & drop',
  'Real-time compliance tracking',
  'Shareable public access links',
  'Full audit trail for every action',
];

export default function LoginPage() {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, signup } = useAuth();
  const inactivityMessage = location.state?.message;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (mode === 'signup') {
      if (!firstName.trim() || !lastName.trim()) {
        setError('First name and last name are required');
        return;
      }
      if (!email.trim()) {
        setError('Email is required');
        return;
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }
    }
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        navigate('/dashboard', { replace: true });
      } else {
        await signup(email, password, firstName.trim(), lastName.trim(), 'admin');
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — matches home page hero gradient */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden items-center justify-center">
        {/* Hero gradient image (same as home page) */}
        <div className="absolute inset-0">
          <img src="/hero-gradient.png" alt="" className="w-full h-full object-cover" />
        </div>
        {/* Subtle dark overlay for text legibility */}
        <div className="absolute inset-0 bg-black/10" />
        {/* Soft floating orbs for depth */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[15%] left-[10%] w-[500px] h-[500px] bg-white/5 rounded-full blur-[100px]" />
          <div className="absolute bottom-[10%] right-[5%] w-[400px] h-[400px] bg-white/5 rounded-full blur-[80px]" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 px-16 max-w-xl"
        >
          <Link to="/" className="flex items-center gap-2.5 mb-12">
            <img src="/logo-white.svg" alt="" className="w-10 h-10" />
            <span className="text-2xl font-bold tracking-tight"><span className="text-white">PAF</span><span className="text-white">Hub</span></span>
          </Link>

          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-6">
            <span className="whitespace-nowrap">H-1B, H-1B1 & E-3</span><br />
            Compliance,<br />
            <span className="bg-gradient-to-r from-indigo-300 to-sky-300 bg-clip-text text-transparent">Simplified.</span>
          </h1>
          <p className="text-white/90 text-lg leading-relaxed mb-12 max-w-md">
            The modern platform for managing Public Access Files, tracking deadlines, and staying audit-ready.
          </p>

          <div className="space-y-4">
            {features.map((feat, i) => (
              <motion.div
                key={feat}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                className="flex items-center gap-3"
              >
                <div className="w-6 h-6 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                </div>
                <span className="text-white text-sm">{feat}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-[#F8FAFF]">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="w-full max-w-[420px]"
        >
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-10">
            <img src="/logo.svg" alt="PAFHub" className="w-10 h-10" />
            <span className="text-xl font-bold tracking-tight"><span className="text-[#000000]">PAF</span><span className="text-[#4F46E5]">Hub</span></span>
          </Link>

          <h2 className="text-2xl font-bold text-[#0F172A] tracking-tight mb-2">
            {mode === 'login' ? 'Welcome back' : 'Create your account'}
          </h2>
          <p className="text-[#64748B] mb-8 text-[15px]">
            {mode === 'login' ? 'Sign in to manage your compliance files' : 'Get started with PAFHub in seconds'}
          </p>

          {inactivityMessage && !error && (
            <div data-testid="inactivity-message" className="mb-6 p-4 bg-amber-50 border border-amber-200 text-amber-700 text-sm font-medium rounded-xl flex items-center gap-3">
              <svg className="w-5 h-5 flex-shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M12 3a9 9 0 110 18 9 9 0 010-18z" /></svg>
              {inactivityMessage}
            </div>
          )}

          {error && (
            <div data-testid="auth-error" className="mb-6 p-4 bg-red-50 border-2 border-red-200 text-red-700 text-sm font-medium rounded-xl flex items-center gap-3 animate-[shake_0.4s_ease-in-out]">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#0F172A] text-sm font-medium mb-1.5 block">First Name</Label>
                  <Input
                    data-testid="signup-firstname-input"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    required
                    className="rounded-xl border-[#E2E8F0] bg-white px-4 py-3 text-[15px] focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all h-12 shadow-sm"
                  />
                </div>
                <div>
                  <Label className="text-[#0F172A] text-sm font-medium mb-1.5 block">Last Name</Label>
                  <Input
                    data-testid="signup-lastname-input"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                    required
                    className="rounded-xl border-[#E2E8F0] bg-white px-4 py-3 text-[15px] focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all h-12 shadow-sm"
                  />
                </div>
              </div>
            )}
            <div>
              <Label className="text-[#0F172A] text-sm font-medium mb-1.5 block">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="rounded-xl border-[#E2E8F0] bg-white px-4 py-3 text-[15px] focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all h-12 shadow-sm"
              />
            </div>
            <div>
              <Label className="text-[#0F172A] text-sm font-medium mb-1.5 block">Password</Label>
              <div className="relative">
                <Input
                  data-testid="login-password-input"
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  minLength={mode === 'signup' ? 6 : undefined}
                  className="rounded-xl border-[#E2E8F0] bg-white px-4 py-3 text-[15px] focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all h-12 pr-10 shadow-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0F172A] transition-colors"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {mode === 'login' && (
              <div className="flex justify-end -mt-1">
                <Link
                  data-testid="forgot-password-link"
                  to="/forgot-password"
                  className="text-sm text-indigo-600 hover:text-indigo-700 hover:underline font-medium"
                >
                  Forgot password?
                </Link>
              </div>
            )}
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-12 font-semibold text-[15px] transition-all shadow-md hover:shadow-lg hover:shadow-indigo-500/25 active:scale-[0.98]"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <span className="flex items-center gap-2">
                  {mode === 'login' ? 'Sign In' : 'Create Account'}
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-[#64748B] mt-8">
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(''); }}
              className="text-indigo-600 hover:text-indigo-700 hover:underline font-semibold"
            >
              {mode === 'login' ? 'Sign Up' : 'Sign In'}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
